import { useCallback, useContext } from 'react';

import isEmpty from 'lodash/isEmpty';

import { RequestAccess } from '@/api/RequestAccess';
import { Users } from '@/api/Users';
import {
  PageResponseUserInfo,
  PostgresFilePointer,
  SuccessResponse,
  UserInfo,
  UserRequestData,
  UsersRequest,
} from '@/api/data-contracts';
import { ROUTES } from '@/config/routes';
import useUploadProgressService from '@/shared/api/uploadProgress.service';
import { UserTypeContext } from '@/shared/lib/UserTypeContext/UserTypeContext';

const useUsersService = () => {
  const userType = useContext(UserTypeContext);
  const usersApi = useUploadProgressService(Users);
  const moduleAccessAPI = useUploadProgressService(RequestAccess);

  const getCurrentUserDetails = useCallback(
    async (signal?: AbortSignal | undefined): Promise<UserInfo> => {
      const { data } = await usersApi.me({ signal });
      return data;
    },
    [usersApi],
  );

  const getAllUsers = async (
    query: {
      request: UsersRequest;
    },
    signal?: AbortSignal | undefined,
  ): Promise<PageResponseUserInfo> => {
    const { data } = await usersApi.allUsers(query.request, {
      signal,
    });
    return data;
  };

  const getUsersByLocations = async (locationIds: string[], signal?: AbortSignal) => {
    const { data } = await usersApi.getUsersByLocations({ locationIds }, { signal });
    return data;
  };

  const getUserDetails = async (
    userId: string,
    signal?: AbortSignal | undefined,
  ): Promise<UserInfo> => {
    const { data } = await usersApi.showUser(userId, { signal });
    return data;
  };

  const updateUser = async (userId: string, form: UserRequestData): Promise<UserInfo> => {
    const { data } = await usersApi.updateUser(userId, form);
    return data;
  };

  const disableUser = async (userId: string): Promise<UserInfo> => {
    const { data } = await usersApi.disableUser(userId);
    return data;
  };

  const addUser = async (form: UserRequestData): Promise<UserInfo> => {
    const { data } = await usersApi.addUser(form);
    return data;
  };

  const impersonateUser = async (userId: string, organizationId: string): Promise<void> => {
    await usersApi.impersonateUser({ userId, organizationId });
    window.location.reload();
  };

  const getUserAvatar = useCallback(
    async (userId: string, signal?: AbortSignal | undefined): Promise<Blob> => {
      const { data } = await usersApi.getUserAvatar(userId, { signal, format: 'blob' });
      return data;
    },
    [usersApi],
  );

  const getCurrentUserAvatar = useCallback(
    async (signal: AbortSignal | undefined): Promise<Blob> => {
      const { data } = await usersApi.getCurrentUserAvatar({ signal, format: 'blob' });
      return data;
    },
    [usersApi],
  );

  const updateUserAvatar = async (userId: string, file: PostgresFilePointer[]): Promise<void> => {
    await usersApi.updateUserAvatar(
      userId,
      { avatar: file },
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  };

  const updateUserAvatarForCurrentUser = async (file: PostgresFilePointer[]): Promise<void> => {
    await usersApi.updateUserAvatarForCurrentUser(
      { avatar: file },
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  };

  const requestAccessToModule = async (
    module: 'LOCATION' | 'ADVERTISER' | 'SERVICE_PROVIDER' | 'BACKOFFICE',
  ): Promise<SuccessResponse> => {
    const { data } = await moduleAccessAPI.requestAccess(module);
    return data;
  };

  const getCurrentUserGuide = async (
    signal?: AbortSignal | undefined,
  ): Promise<{
    guide: { step: number; route: string; tourActive: boolean };
    onboarding: { completed: boolean };
  }> => {
    const { data } = await usersApi.getCurrentUserGuide({ signal });

    if (isEmpty(data)) {
      return {
        guide: {
          step: 0,
          route:
            userType === 'advertiser'
              ? ROUTES.AUTHORIZED.LANDING
              : ROUTES.AUTHORIZED.COMPANY_LOCATIONS,
          tourActive: false,
        },
        onboarding: { completed: false },
      };
    }

    return data;
  };

  const updateUserGuide = async (payload: {
    guide: { step: number; route: string; tourActive: boolean };
    onboarding: { completed: boolean };
  }): Promise<{
    guide: { step: number; route: string; tourActive: boolean };
    onboarding: { completed: boolean };
  }> => {
    const { data } = await usersApi.updateUserGuide(JSON.stringify(payload));
    return data;
  };

  const hasPermission = useCallback(
    async (permission: string) => {
      const user = await getCurrentUserDetails();
      return user.allPermissions.includes(permission) || user.extraPermissions.includes(permission);
    },
    [getCurrentUserDetails],
  );

  return {
    getCurrentUserDetails,
    getAllUsers,
    getUsersByLocations,
    getUserDetails,
    updateUser,
    disableUser,
    addUser,
    impersonateUser,
    getUserAvatar,
    getCurrentUserAvatar,
    updateUserAvatar,
    updateUserAvatarForCurrentUser,
    requestAccessToModule,
    getCurrentUserGuide,
    updateUserGuide,
    hasPermission,
  };
};

export default useUsersService;
